// libs/shared/NetworkStatusAlert.tsx

import React from "react";
import { Alert, IconButton, Typography, Box } from "@mui/material";
import {
  WifiOff as WifiOffIcon,
  SignalCellularAlt as SignalIcon,
  Refresh as RefreshIcon,
  ErrorOutline as ErrorIcon,
} from "@mui/icons-material";
import { useNetworkQuality } from "../../../hooks";

interface NetworkStatusAlertProps {
  /**
   * Función para refrescar datos cuando hay conexión
   */
  onRefresh?: () => void;
  
  /**
   * Mostrar botón de refrescar en alertas
   */
  showRefreshButton?: boolean;
  
  /**
   * Personalizar mensaje de conexión lenta
   */
  slowConnectionMessage?: string;
  
  /**
   * Personalizar mensaje de offline
   */
  offlineMessage?: string;
  
  /**
   * Personalizar mensaje de error
   */
  errorMessage?: string;
  
  /**
   * Ocultar la alerta cuando la conexión es buena
   */
  hideOnGoodConnection?: boolean;
  
  /**
   * Mostrar latencia en el mensaje
   */
  showLatency?: boolean;

  /**
   * Clase CSS personalizada
   */
  className?: string;

  /**
   * Estilos personalizados
   */
  sx?: any;
}

export const NetworkStatusAlert: React.FC<NetworkStatusAlertProps> = ({
  onRefresh,
  showRefreshButton = true,
  slowConnectionMessage,
  offlineMessage,
  errorMessage,
  hideOnGoodConnection = true,
  showLatency = true,
  className,
  sx,
}) => {
  const { networkState, checkNetworkQuality } = useNetworkQuality();

  const isOffline = networkState.quality === "offline";
  const isSlowConnection = networkState.quality === "slow";
  const hasError = networkState.quality === "error";
  const isGood = networkState.quality === "good";

  const handleRefresh = () => {
    checkNetworkQuality();
    onRefresh?.();
  };

  // No mostrar nada si la conexión es buena y hideOnGoodConnection está activo
  if (isGood && hideOnGoodConnection) {
    return null;
  }

  // Alerta de Sin Conexión
  if (isOffline) {
    return (
      <Alert
        severity="error"
        icon={<WifiOffIcon />}
        className={className}
        sx={{ mb: 2, ...sx }}
        action={
          showRefreshButton && (
            <IconButton
              color="inherit"
              size="small"
              onClick={handleRefresh}
              aria-label="refrescar"
            >
              <RefreshIcon />
            </IconButton>
          )
        }
      >
        <Typography variant="body2">
          {offlineMessage || "Sin conexión a internet. No se pueden cargar los datos."}
        </Typography>
      </Alert>
    );
  }

  // Alerta de Conexión Lenta
  if (isSlowConnection) {
    const latencyText = showLatency && networkState.latency 
      ? ` (latencia: ${networkState.latency}ms)` 
      : "";

    return (
      <Alert
        severity="warning"
        icon={<SignalIcon />}
        className={className}
        sx={{ mb: 2, ...sx }}
        action={
          showRefreshButton && (
            <IconButton
              color="inherit"
              size="small"
              onClick={handleRefresh}
              aria-label="refrescar"
            >
              <RefreshIcon />
            </IconButton>
          )
        }
      >
        <Typography variant="body2">
          {slowConnectionMessage || `Conexión lenta detectada${latencyText}. Las operaciones pueden tardar más de lo habitual.`}
        </Typography>
      </Alert>
    );
  }

  // Alerta de Error
  if (hasError) {
    return (
      <Alert
        severity="error"
        icon={<ErrorIcon />}
        className={className}
        sx={{ mb: 2, ...sx }}
        action={
          showRefreshButton && (
            <IconButton
              color="inherit"
              size="small"
              onClick={handleRefresh}
              aria-label="refrescar"
            >
              <RefreshIcon />
            </IconButton>
          )
        }
      >
        <Typography variant="body2">
          {errorMessage || `Error de conexión: ${networkState.error || "Error desconocido"}`}
        </Typography>
      </Alert>
    );
  }

  // Alerta de Conexión Buena (solo si hideOnGoodConnection es false)
  if (isGood && !hideOnGoodConnection) {
    const latencyText = showLatency && networkState.latency 
      ? ` (${networkState.latency}ms)` 
      : "";

    return (
      <Alert
        severity="success"
        icon={<SignalIcon />}
        className={className}
        sx={{ mb: 2, ...sx }}
      >
        <Typography variant="body2">
          Conexión estable{latencyText}
        </Typography>
      </Alert>
    );
  }

  return null;
};

// Componente alternativo más compacto para la toolbar
export const NetworkStatusIndicator: React.FC<{
  onClick?: () => void;
  showTooltip?: boolean;
}> = ({ onClick, showTooltip = true }) => {
  const { networkState } = useNetworkQuality();

  const isOffline = networkState.quality === "offline";
  const isSlowConnection = networkState.quality === "slow";
  const hasError = networkState.quality === "error";
  const isGood = networkState.quality === "good";

  const getTooltipText = () => {
    if (isOffline) return "Sin conexión";
    if (isSlowConnection) return `Conexión lenta (${networkState.latency || "N/A"}ms)`;
    if (hasError) return "Error de conexión";
    return `Conexión estable (${networkState.latency || "N/A"}ms)`;
  };

  const getIcon = () => {
    if (isOffline) return <WifiOffIcon color="error" />;
    if (isSlowConnection) return <SignalIcon color="warning" />;
    if (hasError) return <ErrorIcon color="error" />;
    return <SignalIcon color="success" />;
  };

  if (showTooltip) {
    return (
      <Box
        component="span"
        title={getTooltipText()}
        onClick={onClick}
        sx={{ 
          cursor: onClick ? 'pointer' : 'default',
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        {getIcon()}
      </Box>
    );
  }

  return (
    <Box
      component="span"
      onClick={onClick}
      sx={{ 
        cursor: onClick ? 'pointer' : 'default',
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      {getIcon()}
    </Box>
  );
};

// Hook personalizado para obtener el estado de red de forma simple
export const useNetworkStatus = () => {
  const { networkState } = useNetworkQuality();

  return {
    isOffline: networkState.quality === "offline",
    isSlowConnection: networkState.quality === "slow",
    hasError: networkState.quality === "error",
    isGood: networkState.quality === "good",
    latency: networkState.latency,
    error: networkState.error,
    quality: networkState.quality,
  };
};

export default NetworkStatusAlert;